import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import React, { useEffect, useRef, useState } from 'react'
import { n_ai_tutor, n_common } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { RECORDING_STATUS } from '../../../../modules/vars'
import { ConversationErrorPopup } from '../../../new-portal/conversational-ai/ConversationPopup'
import LoadingSpinner from '../../LoadingSpinner'
import { addEventListeners, getPageVisibilityAPI, isIOSDevice, removeEventListeners } from '../play/unmute'
import Recognition from './Recognition'
import StartRecord from './StartRecord'
import { createAudioTranscriber } from './transcriber'

const NoRecognizedModal = ({ show, setShow }) => {
  const { t } = useTranslation()
  return (
    <DSModal id="aiTutorQuitConfirm" isOpen={show} setIsOpen={setShow} size={'sm'} closeButton={false}>
      <DSModal.Content className="mt-[16px]">{t(n_ai_tutor.common.popup_body1)}</DSModal.Content>
      <DSModal.ButtonContainer direction="horizontal">
        <DSButton
          size={BUTTON_SIZE.MD}
          type={BUTTON_TYPE.SOLID}
          onClick={() => {
            setShow(false)
          }}
        >
          <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

const useTimeout = (f, timeout, deps) => {
  useEffect(() => {
    const timeoutId = setTimeout(f, timeout)
    return () => clearTimeout(timeoutId)
  }, [f, timeout, deps])
}

const Record = ({
  isSessionClosing,
  handleStopRecording: onRecognitionComplete,
  setRecordingStatus,
  onStartRecord: onStarted,
  onFinishRecord: onStopped,
  beforeRecord: onStarted2,
  afterRecord: onRecordingComplete,
  handleSessionSummary,
  showNudge,
  recordRef,
  isLoading: externalLoading,
  className = '',
  autoStart = false,
  handleCancel: onRecordingCancelled = null,
  helper = null,
  noMargin = false,
  wordCount = 0,
  errorModal = false,
}: any) => {
  const [recognizing, setRecognizing] = useState<'requested' | 'recording' | 'idle'>('idle')
  const [transcription, setTranscription] = useState('')
  const [waveformData, setWaveFormData] = useState([])
  const [waitingTranscription, setWaitingTranscription] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [showNoRecognizedModal, setShowNoRecognizedModal] = useState(false)
  const [amplitude, setAmplitude] = useState(0)

  const [localRecordingStatus, setLocalRecordingStatus] = useState<any>('idle')
  const [recordPayload, setRecordPayload] = useState<any>(null)
  const loading = externalLoading

  const transcriberRef = useRef<ReturnType<typeof createAudioTranscriber>>(null)
  if (!transcriberRef.current) {
    transcriberRef.current = createAudioTranscriber()
  }

  const resetRecordingState = () => {
    setRecognizing('idle')
    setWaitingTranscription(false)
    setTranscription('')
  }

  const bands = 5
  const thresholds = Array.from({ length: bands - 1 }, (_, i) => (i + 1) / bands)

  function generateRandomId(length = 8): string {
    // toString(36) turns numbers into 0–9a–z. slice off the "0." prefix.
    return Math.random()
      .toString(36)
      .substring(2, 2 + length)
  }

  useEffect(() => {
    function handleMessage(event) {
      let data
      try {
        data = JSON.parse(event.data)
      } catch (_e) {
        // console.error('Invalid message format:', e)
        return
      }

      if (data.type === 'recording') {
        setLocalRecordingStatus('recording')
      }

      if (data.type === 'audioEnded') {
        // handleAudioEnded(data.url)
      }

      if (data.type === 'canceled') {
        setLocalRecordingStatus('idle')
      }

      if (data.type === 'recordingResult') {
        setRecordPayload({
          id: generateRandomId(), // e.g. "xk3j5b9q"
          transcription: data.transcription,
          audioBlob: null, // RN 쪽에서 blob 처리한다면 RN 메시지로 받을 수도 있고,
        })

        setLocalRecordingStatus('idle')
      }
    }

    window.addEventListener('message', handleMessage)
    document.addEventListener('message', handleMessage) // iOS 대응

    // 클린업
    return () => {
      window.removeEventListener('message', handleMessage)
      document.removeEventListener('message', handleMessage)
    }
  }, [])

  // function handleAudioEnded(url: string) {
  //   // setPlayingAudio(false)    setPlaying(false
  //   // setPlaying(false)
  //   // setPlayingMap((prev) => ({ ...prev, [url]: false }))
  //   // // ② currPlay가 바로 이 URL이면 인덱스 리셋
  //   // setCurrPlay(null)
  // }

  useEffect(() => {
    if (!recordPayload) return

    // payload가 들어올 때마다 이펙트에서 처리

    console.log('===================>', recordPayload.transcription)
    if (recordPayload.transcription?.split('').length > 1) {
      onRecognitionComplete(recordPayload.transcription, recordPayload.audioBlob)
    } else {
      // alert('error')

      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: JSON.stringify({ data: recordPayload.transcription }) })
      )
    }

    // (선택) 반복 처리 방지를 위해 페이로드 초기화
    setRecordPayload(null)
  }, [recordPayload])

  // useEffect(() => {
  //   return () => {
  //     onCancel()
  //   }
  // }, [])

  const startRecording = async () => {
    onStarted?.()
    onStarted2?.()
    setRecordingStatus?.(RECORDING_STATUS.START_RECORDING)
    const transcriber = transcriberRef.current
    try {
      setRecognizing('requested')

      const [startedResult, endedResult] = await transcriberRef.current.startRecognition({
        onWaveformData: (audio) => {
          if (transcriberRef.current !== transcriber) {
            return
          }
          setWaveFormData(audio)

          const overallAmp = audio.map((v) => Math.abs(v)).reduce((sum, v) => sum + v, 0) / audio.length

          // 필요에 따라 setWaveFormData에 숫자 하나만 넣거나
          // [overallAmp] 형태로 넣어주세요

          let band = 0
          if (overallAmp > thresholds[0]) {
            // thresholds[i] 보다 작으면 i+1 band
            band = thresholds.findIndex((t) => overallAmp <= t) + 1
            // findIndex 가 -1 (모두 작음) 이면 band = thresholds.length
            if (band === 0) band = thresholds.length
          }
          // setAmplitudeBand(band);

          setAmplitude(band)
        },
        onTranscription: (text) => {
          if (transcriberRef.current !== transcriber) {
            return
          }
          setTranscription(text)
        },
      })
      setRecognizing('recording')
      await startedResult
      const result = await endedResult
      if (transcriberRef.current !== transcriber) {
        return
      }
      onStopped?.()
      if (!result.completed) {
        return
      }
      const { text, wav: audio } = result.data
      onRecordingComplete?.([text, audio])
      if (!text) {
        setShowNoRecognizedModal(true)
        cancelRecording()
        return
      }
      onRecognitionComplete(text, audio)
    } catch (e) {
      setShowErrorModal(true)
      throw e
    } finally {
      if (transcriberRef.current === transcriber) {
        resetRecordingState()
      }
    }
  }

  const cancelRecording = async () => {
    await transcriberRef.current.stopRecognition(false)
    onRecordingCancelled?.()
  }

  const stopRecording = async () => {
    const transcriber = transcriberRef.current
    try {
      const [text, audio]: [string, ArrayBuffer | null] = await transcriberRef.current.stopRecognition(true)
    } catch (e) {
      setShowErrorModal(true)
      throw e
    } finally {
      if (transcriberRef.current === transcriber) {
        resetRecordingState()
        transcriberRef.current = createAudioTranscriber()
      }
    }
  }

  useTimeout(
    () => {
      if (recognizing && !transcription) {
        cancelRecording()
        console.log('TIMEOUT without transcription')
      }
    },
    30000,
    [recognizing, transcription]
  )

  useEffect(() => {
    const handleVisibilityChange = () => {
      cancelRecording()
    }

    const handleWindowFocusChange = (e) => {
      if (e.type === 'blur') cancelRecording()
    }

    const pageVisibilityAPI = getPageVisibilityAPI()
    const isIOS: boolean = isIOSDevice()
    if (pageVisibilityAPI)
      addEventListeners(document, [pageVisibilityAPI.visibilitychange], handleVisibilityChange, true, true)
    if (isIOS) addEventListeners(window, ['focus', 'blur'], handleWindowFocusChange, true, true)

    return () => {
      if (pageVisibilityAPI)
        removeEventListeners(document, [pageVisibilityAPI.visibilitychange], handleVisibilityChange, true, true)
      if (isIOS) removeEventListeners(window, ['focus', 'blur'], handleWindowFocusChange, true, true)
    }
  }, [recognizing])

  useEffect(() => {
    if (autoStart) {
      startRecording()
    }
  }, [autoStart])

  useEffect(() => {
    return () => {
      console.log('UNMOUNTED')
      transcriberRef.current.stopRecognition(false).catch((e: any) => console.error(e))
    }
  }, [])

  const onCancel = () => {
    try {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'cancelRecording' }))
    } catch (e) {
      console.log(e)
    }
    setLocalRecordingStatus('cancelling')
  }

  // 밑에를 복구 하면 멀티 캠퍼스가 됨.
  const renderRecordingButton = () => {
    const loadingNode: React.ReactNode = <LoadingSpinner lg show primary />

    if (waitingTranscription || recognizing === 'requested') {
      return loadingNode
    }

    if (recognizing === 'recording' && waveformData.length === 0) {
      return loadingNode
    } else if (recognizing === 'recording') {
      return (
        <Recognition
          onClickRestart={cancelRecording}
          onClickHandleStopRecording={stopRecording}
          waveformData={waveformData}
          amplitude={amplitude}
          waitingTranscription={waitingTranscription}
        />
      )
    }

    return (
      <StartRecord
        onClickStart={startRecording}
        loading={externalLoading}
        isSessionClosing={isSessionClosing}
        handleSessionSummary={handleSessionSummary}
        showNudge={showNudge}
        helper={helper}
      />
    )
  }

  return (
    <div
      className={`bg-white opacity-95 flex flex-col bottom-[0px] items-center justify-center ${
        noMargin ? (recognizing ? '' : '') : 'pt-[12px] px-[34px] pb-[52px]'
      } mt-[auto] ${className}`}
      style={{
        width: '100%',
        height: 148,
        paddingBottom: `calc(${noMargin ? (recognizing ? '0px' : '0px') : '32px'} + env(safe-area-inset-bottom))`,
      }}
      ref={recordRef}
    >
      {renderRecordingButton()}
      {errorModal && <ConversationErrorPopup showErrorModal={showErrorModal} setShowErrorModal={setShowErrorModal} />}
      <NoRecognizedModal show={showNoRecognizedModal} setShow={setShowNoRecognizedModal} />
    </div>
  )
}

export default Record
